import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import FacebookIcon from "@mui/icons-material/Facebook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HandshakeIcon from "@mui/icons-material/Handshake";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Typography from "@mui/material/Typography";
import { CRM_SETUP_STEPS } from "../constants/setupSteps";
import type { CrmSetupStepId } from "../types";
import CrmSetupConfigurationStep from "./steps/CrmSetupConfigurationStep";
import CrmSetupIntroductionStep from "./steps/CrmSetupIntroductionStep";
import CrmSetupSendTestStep from "./steps/CrmSetupSendTestStep";
import CrmSetupServiceContractStep from "./steps/CrmSetupServiceContractStep";
import type { UseCrmSetupConfigurationResult } from "../hooks/useCrmSetupConfiguration";

const stepIdToIconMap: Record<CrmSetupStepId, ReactNode> = {
  introduction: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
  configuration: <FacebookIcon sx={{ fontSize: 20 }} />,
  sendTest: <WhatsAppIcon sx={{ fontSize: 20 }} />,
  serviceContract: <HandshakeIcon sx={{ fontSize: 20 }} />,
};

export interface CrmSetupStepperSectionProps {
  organizationId?: string;
  practitionerId?: string;
  currentStepId: CrmSetupStepId;
  onGoToStep: (stepId: CrmSetupStepId) => void;
  onComplete: () => void;
  /** Mesma instância do hook usada na página (evita estado duplicado). */
  setupConfiguration: UseCrmSetupConfigurationResult;
  /**
   * Passo configuração: só libera "Próximo" com Fluxmed selecionado ou retorno completo da Meta (WABA).
   * Default true se omitido.
   */
  isCrmConfigurationAccountsConfigured?: boolean;
  isCrmConfigurationLoading?: boolean;
  /**
   * Hook para executar efeitos reais antes de avançar (ex.: persistir Endpoint Meta).
   *
   * Regra:
   * - Deve rodar apenas em ações explícitas do usuário (click em "Próximo").
   * - O stepper aguarda (`await`) antes de avançar.
   */
  onBeforeNext?: (currentStepId: CrmSetupStepId) => void | Promise<void>;
  isBeforeNextLoading?: boolean;
}

const CrmSetupStepperSection = memo(
  ({
    organizationId,
    practitionerId,
    currentStepId,
    onGoToStep,
    onComplete,
    setupConfiguration,
    isCrmConfigurationAccountsConfigured = true,
    isCrmConfigurationLoading = false,
    onBeforeNext,
    isBeforeNextLoading = false,
  }: CrmSetupStepperSectionProps) => {
    const selectedOption =
      setupConfiguration.configurationStatus?.selectedOption ?? null;

    /**
     * Importante:
     * - O step "Teste de envio" só existe quando a opção selecionada é o padrão Fluxmed.
     * - Mantemos o array global `CRM_SETUP_STEPS` intacto e filtramos em runtime para evitar
     *   efeitos colaterais em outras telas/fluxos.
     */
    const visibleSteps = useMemo(() => {
      const isFluxmedDefault = selectedOption === "fluxmedDefault";
      if (isFluxmedDefault) return CRM_SETUP_STEPS;
      return CRM_SETUP_STEPS.filter((step) => step.id !== "sendTest");
    }, [selectedOption]);

    const isNextDisabledOnConfigurationStep =
      currentStepId === "configuration" &&
      (isCrmConfigurationLoading || !isCrmConfigurationAccountsConfigured);

    const activeStepIndex = useMemo(() => {
      const currentIndex = visibleSteps.findIndex(
        (step) => step.id === currentStepId,
      );
      return currentIndex >= 0 ? currentIndex : 0;
    }, [currentStepId, visibleSteps]);

    const isLastStep = activeStepIndex === visibleSteps.length - 1;

    const isSendTestStepVisible = useMemo(() => {
      return visibleSteps.some((step) => step.id === "sendTest");
    }, [visibleSteps]);

    useEffect(() => {
      if (currentStepId !== "sendTest") return;
      if (isSendTestStepVisible) return;
      // Fallback seguro: se alguém cair no step removido, volta para "configuração"
      // (ou primeiro step visível, se por algum motivo não existir).
      const fallbackStepId =
        visibleSteps.find((step) => step.id === "configuration")?.id ??
        visibleSteps[0]?.id ??
        "introduction";
      onGoToStep(fallbackStepId);
    }, [currentStepId, isSendTestStepVisible, onGoToStep, visibleSteps]);

    const handleBack = useCallback(() => {
      const previousIndex = activeStepIndex - 1;
      if (previousIndex < 0) return;
      onGoToStep(visibleSteps[previousIndex].id);
    }, [activeStepIndex, onGoToStep, visibleSteps]);

    const handleNext = useCallback(async () => {
      const nextIndex = activeStepIndex + 1;
      if (nextIndex >= visibleSteps.length) return;
      await onBeforeNext?.(currentStepId);
      onGoToStep(visibleSteps[nextIndex].id);
    }, [
      activeStepIndex,
      currentStepId,
      onBeforeNext,
      onGoToStep,
      visibleSteps,
    ]);

    const activeStepComponent = useMemo(() => {
      if (currentStepId === "configuration") {
        return <CrmSetupConfigurationStep configuration={setupConfiguration} />;
      }
      if (currentStepId === "sendTest") {
        // O step só deve ser acessível quando Fluxmed (default) estiver selecionado.
        if (!isSendTestStepVisible) {
          return (
            <CrmSetupConfigurationStep configuration={setupConfiguration} />
          );
        }
        return (
          <CrmSetupSendTestStep
            organizationId={organizationId}
            practitionerId={practitionerId}
          />
        );
      }
      if (currentStepId === "serviceContract") {
        return <CrmSetupServiceContractStep organizationId={organizationId} />;
      }
      return <CrmSetupIntroductionStep />;
    }, [
      currentStepId,
      isSendTestStepVisible,
      organizationId,
      practitionerId,
      setupConfiguration,
    ]);

    return (
      <Stack spacing={2}>
        {/* Header da tela (mesmo padrão visual do `SchedulePage`) */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AutoAwesomeIcon sx={{ fontSize: 32, color: "primary.main" }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                FluxZap
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Primeiro acesso: vincule Meta/WhatsApp e prepare automações para
                padronizar o atendimento.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: "divider", opacity: 0.8 }} />

        {/* Stepper customizado: ícone + conectores + rótulo (sem depender do Stepper do MUI) */}
        <Box sx={{ overflowX: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${visibleSteps.length * 2 - 1}, minmax(92px, 1fr))`,
              alignItems: "center",
              columnGap: 0.25,
              pb: 0.5,
            }}
          >
            {visibleSteps.map((setupStep, setupStepIndex) => {
              const isCompleted = setupStepIndex < activeStepIndex;
              const isActive = setupStepIndex === activeStepIndex;
              // Bordas e conectores sempre em primary.
              // O que muda no concluído é apenas o ícone (check) para melhorar percepção.
              const accentColor = "primary.main";

              const label = setupStep.label;
              const icon = stepIdToIconMap[setupStep.id];
              const isNotLastStep = setupStepIndex < visibleSteps.length - 1;

              return (
                <Fragment key={setupStep.id}>
                  <Stack
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      minHeight: 72,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        border: "2px solid",
                        borderColor: accentColor,
                        color: isActive ? "common.white" : accentColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isActive
                          ? "primary.main"
                          : isCompleted
                            ? "background.paper"
                            : "background.default",
                        boxShadow: "none",
                        flexShrink: 0,
                      }}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon
                          sx={{ fontSize: 22, color: "primary.main" }}
                        />
                      ) : (
                        icon
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 800 : 700}
                      sx={{
                        fontSize: { xs: 11, sm: 12 },
                        textTransform: "none",
                        lineHeight: 1.25,
                        textAlign: "center",
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "unset",
                        maxWidth: 140,
                        wordBreak: "break-word",
                        color: isActive ? "primary.main" : "text.primary",
                      }}
                      title={label}
                    >
                      {label}
                    </Typography>
                  </Stack>

                  {isNotLastStep && (
                    <Box
                      sx={{
                        height: 64,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 2,
                          borderRadius: 999,
                          bgcolor: "primary.main",
                          opacity: isCompleted ? 1 : 0.35,
                        }}
                      />
                    </Box>
                  )}
                </Fragment>
              );
            })}
          </Box>
        </Box>

        {activeStepComponent}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStepIndex === 0}
          >
            Voltar
          </Button>

          {isLastStep ? (
            <Button variant="contained" onClick={onComplete}>
              Finalizar e acessar painel
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                isBeforeNextLoading || isNextDisabledOnConfigurationStep
              }
            >
              {isBeforeNextLoading ? "Salvando..." : "Próximo"}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  },
);

CrmSetupStepperSection.displayName = "CrmSetupStepperSection";

export default CrmSetupStepperSection;
